import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Calendar, Upload, DollarSign, Wallet, Download } from 'lucide-react';
import { Users, Building } from '@phosphor-icons/react';
import api, { staffAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const DEPARTMENTS = [
  'MUHASEBE', 'MUTFAK', 'FIRIN', 'KASA', 'PAKETÇİ', 'GARSON', 'BULAŞIKÇI', 'TEMİZLİK', 'Diğer'
];

const DAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

export default function Staff() {
  const { user } = useAuth();
  const [staffList, setStaffList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    department: 'MUTFAK',
    salary: '',
    employment_type: 'resmi',
    day_off: '',
    iban: '',
    sgk_no: '',
    start_date: '',
    notes: ''
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [staffRes, summaryRes] = await Promise.all([
        api.get('/staff'),
        api.get('/staff/summary')
      ]);
      setStaffList(staffRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Personel adı gerekli');
      return;
    }
    if (!formData.salary || parseFloat(formData.salary) <= 0) {
      toast.error('Geçerli bir maaş girin');
      return;
    }

    try {
      const payload = {
        ...formData,
        salary: parseFloat(formData.salary)
      };

      if (editingStaff) {
        await api.put(`/staff/${editingStaff.id}`, payload);
        toast.success('Personel güncellendi');
      } else {
        await api.post('/staff', payload);
        toast.success('Personel eklendi');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu personeli silmek istediğinize emin misiniz?')) return;

    try {
      await api.delete(`/staff/${id}`);
      toast.success('Personel silindi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    }
  };

  const handleEdit = (staff) => {
    setEditingStaff(staff);
    setFormData({
      name: staff.name,
      department: staff.department,
      salary: staff.salary.toString(),
      employment_type: staff.employment_type,
      day_off: staff.day_off || '',
      iban: staff.iban || '',
      sgk_no: staff.sgk_no || '',
      start_date: staff.start_date || '',
      notes: staff.notes || ''
    });
    setIsDialogOpen(true);
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/staff/import/excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.warning(err));
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import başarısız');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${backendUrl}/api/staff/template/excel`, '_blank');
    toast.success('Şablon indiriliyor...');
  };

  const resetForm = () => {
    setEditingStaff(null);
    setFormData({
      name: '',
      department: 'MUTFAK',
      salary: '',
      employment_type: 'resmi',
      day_off: '',
      iban: '',
      sgk_no: '',
      start_date: '',
      notes: ''
    });
  };

  const handleTransferSalaries = async () => {
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    
    if (!confirm(`${month} ayı için tüm maaşları gidere aktarmak istiyor musunuz?`)) return;
    
    try {
      const res = await staffAPI.transferSalaries(month);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Maaşlar aktarılamadı');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight">Personel Yönetimi</h1>
            <p className="text-muted-foreground mt-1">Personel listesi ve maaş takibi</p>
          </div>
          
          <div className="flex gap-2">
            {isAdmin && (
              <>
                <Button variant="outline" onClick={handleTransferSalaries} data-testid="transfer-salaries-btn">
                  <Wallet className="w-4 h-4 mr-2" />
                  Maaşları Gidere Aktar
                </Button>
                
                <Button variant="outline" onClick={handleDownloadTemplate}>
                  <Download className="w-4 h-4 mr-2" />
                  Şablon İndir
                </Button>
                
                <div className="relative">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleImportExcel}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={isUploading}
                  />
                  <Button variant="outline" disabled={isUploading} className="bg-green-50 border-green-200 hover:bg-green-100 text-green-700">
                    <Upload className="w-4 h-4 mr-2" />
                    {isUploading ? 'Yükleniyor...' : 'Excel Yükle'}
                  </Button>
                </div>
              </>
            )}
            
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm} data-testid="add-staff-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Personel Ekle
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingStaff ? 'Personel Düzenle' : 'Yeni Personel'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Ad Soyad</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Personel adı"
                        data-testid="staff-name-input"
                      />
                    </div>
                    
                    <div>
                      <Label>Bölüm</Label>
                      <Select value={formData.department} onValueChange={(v) => setFormData({ ...formData, department: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DEPARTMENTS.map((d) => (
                            <SelectItem key={d} value={d}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Aylık Maaş (₺)</Label>
                      <Input
                        type="number"
                        value={formData.salary}
                        onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                        placeholder="0.00"
                        data-testid="staff-salary-input"
                      />
                    </div>
                    
                    <div>
                      <Label>Çalışma Türü</Label>
                      <Select value={formData.employment_type} onValueChange={(v) => setFormData({ ...formData, employment_type: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="resmi">Resmi</SelectItem>
                          <SelectItem value="gayri_resmi">Gayri Resmi</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>İzin Günü</Label>
                      <Select value={formData.day_off} onValueChange={(v) => setFormData({ ...formData, day_off: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS.map((d) => (
                            <SelectItem key={d} value={d}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>SGK No</Label>
                      <Input
                        value={formData.sgk_no}
                        onChange={(e) => setFormData({ ...formData, sgk_no: e.target.value })}
                        placeholder="SGK numarası"
                      />
                    </div>
                    
                    <div>
                      <Label>İşe Başlama</Label>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Label>IBAN</Label>
                      <Input
                        value={formData.iban}
                        onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                        placeholder="TR..."
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Label>Notlar</Label>
                      <Input
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Ek notlar..."
                      />
                    </div>
                  </div>
                  
                  <Button onClick={handleSubmit} className="w-full" data-testid="save-staff-btn">
                    {editingStaff ? 'Güncelle' : 'Kaydet'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Users className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Toplam Personel</p>
                    <p className="text-2xl font-bold">{summary.active_staff}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <DollarSign className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Aylık Toplam</p>
                    <p className="text-2xl font-bold">{formatCurrency(summary.total_monthly_salary)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/10 rounded-lg">
                    <Calendar className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Günlük Gider</p>
                    <p className="text-2xl font-bold">{formatCurrency(summary.total_daily_cost)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Building className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Bölüm Sayısı</p>
                    <p className="text-2xl font-bold">{Object.keys(summary.by_department || {}).length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Department Summary */}
        {summary?.by_department && Object.keys(summary.by_department).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Bölüm Bazında Özet</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(summary.by_department).map(([dept, data]) => (
                  <div key={dept} className="p-3 bg-muted/50 rounded-lg">
                    <p className="font-medium text-sm">{dept}</p>
                    <p className="text-lg font-bold">{data.count} kişi</p>
                    <p className="text-xs text-muted-foreground">
                      Günlük: {formatCurrency(data.daily_cost)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Staff Table */}
        <Card>
          <CardHeader>
            <CardTitle>Personel Listesi</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : staffList.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Henüz personel kaydı yok
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ad Soyad</TableHead>
                    <TableHead>Bölüm</TableHead>
                    <TableHead>Çalışma Türü</TableHead>
                    <TableHead className="text-right">Aylık Maaş</TableHead>
                    <TableHead className="text-right">Günlük</TableHead>
                    <TableHead>İzin Günü</TableHead>
                    <TableHead className="text-center">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffList.map((staff) => (
                    <TableRow key={staff.id} data-testid={`staff-row-${staff.id}`}>
                      <TableCell className="font-medium">{staff.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{staff.department}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={staff.employment_type === 'resmi' ? 'bg-green-500' : 'bg-orange-500'}>
                          {staff.employment_type === 'resmi' ? 'Resmi' : 'Gayri Resmi'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(staff.salary)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(staff.daily_cost)}</TableCell>
                      <TableCell>{staff.day_off || '-'}</TableCell>
                      <TableCell>
                        <div className="flex justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(staff)}
                            data-testid={`edit-staff-${staff.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(staff.id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-staff-${staff.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
